import { ReactElement, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { splitGamesIntoUnits } from './grid-utils';
import { isSmallerSelector } from '~/components/layout/mediaquery';
import { GridsContainer, Grid, CardContainer } from './grid.styles';

const cardsMap = {
	1: 'big',
	2: 'big',
	3: 'small',
	4: 'small',
	5: 'small'
} as const;

const cardPositions = {
	1: `
		grid-column: 1 / 9;
		grid-row: 1;
	`,
	2: `
		grid-column: 9 / 17;
		grid-row: 1;
	`,
	3: `
		grid-column: 1 / 6;
		grid-row: 2;
	`,
	4: `
		grid-column: 6 / 12;
		grid-row: 2;
	`,
	5: `
		grid-column: 12 / 17;
		grid-row: 2;
	`
};

type GridType1Props = {
	allowIncompleteUnits?: boolean;
};

const GridType1 = <T extends ReactElement<unknown>[]>({
	GameCards,
	allowIncompleteUnits = false
}: { GameCards: T } & GridType1Props) => {
	const isSmaller = useSelector(isSmallerSelector);

	const itemsPerUnit = 5;

	const units = useMemo(
		() => splitGamesIntoUnits(GameCards, itemsPerUnit, allowIncompleteUnits),
		[GameCards, itemsPerUnit, allowIncompleteUnits]
	);

	const gridSizes = useMemo(
		() => ({
			width: isSmaller ? '336px' : '510px',
			height: isSmaller ? '278px' : '423px'
		}),
		[isSmaller]
	);

	const cardSizes = useMemo(
		() => ({
			big: {
				width: isSmaller ? '162px' : '249px',
				height: isSmaller ? '162px' : '249px'
			},
			small: {
				width: isSmaller ? '104px' : '162px',
				height: isSmaller ? '104px' : '162px'
			}
		}),
		[isSmaller]
	);

	return (
		<GridsContainer>
			{units.map((cards, unitIndex) => (
				<Grid
					// eslint-disable-next-line react/no-array-index-key
					key={`unit-${unitIndex}`}
					width={gridSizes.width}
					height={gridSizes.height}
				>
					{cards.map((card, cardIndex) => (
						<CardContainer
							key={card.key}
							size={cardSizes[cardsMap[(cardIndex + 1) as keyof typeof cardsMap]]}
							position={cardPositions[(cardIndex + 1) as keyof typeof cardsMap]}
						>
							{card}
						</CardContainer>
					))}
				</Grid>
			))}
		</GridsContainer>
	);
};

export default GridType1;
